import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Text, Spacing, Alert, Icon } from '@reservamos/elements';
import SectionTitle from 'ui/atoms/SectionTitle';
import Loader from 'ui/atoms/Loader';
import TicketSummaryContainer from 'ui/molecules/TicketSummaryContainer';
import avatarForTicket from 'utils/exchangeTicketAvatars';
import resolveSelectedExchangeTicket from 'utils/exchangeSelectedTicket';
import { useTranslation } from 'react-i18next';
import { executeRecaptcha } from 'utils/googleRecaptcha';
import { fetchExchangeableLines, resetExchange } from '../../../actions/exchange';
import { createSchedulePath } from '../../../utils/exchange';
import ExchangeTypeahead from '../ExchangeTypeahead';
import ExchangeTripList from '../ExchangeTripList';
import DatePicker from '../../DatePicker';
import ExchangeDatePicker from '../ExchangeDatePicker';

const ExchangeSecondStep = ({
  isFetching,
  token,
  hasSelectedSeats,
  originSlug,
  originDisplay,
  destinationDisplay,
  destinationSlug,
  date,
  currentTicket,
  clearTypeahead,
  errorTrip,
  errorTripExchange,
  validateExchange,
  setDate,
}) => {
  const { t } = useTranslation('exchange');
  const { features, env } = useSelector((state) => state.whitelabelConfig);

  const useNewExchangeUi = Boolean(features.USE_NEW_EXCHANGE_UI);
  // Mirrors `ExchangeTripList`: the panel renders the search widget on both paths, so the legacy
  // typeahead and date picker step aside rather than duplicating the same controls above it.
  const showSearchWidget = Boolean(features.USE_SEARCH_WIDGET_ON_EXCHANGE) || useNewExchangeUi;

  // Selected field by field: subscribing to the whole `exchange` slice would re-render this step
  // and the trip list below it on every unrelated exchange action (loading toggles, line fetches),
  // since the reducer returns a new slice object each time.
  const allPassengers = useSelector((state) => state.exchange.allPassengers);
  const trips = useSelector((state) => state.exchange.trips);
  const lines = useSelector((state) => state.exchange.lines);
  const linesLoading = useSelector((state) => state.exchange.linesLoading);
  const confirmedTransporter = useSelector((state) => state.exchange.transporter);
  // Both come from what `exchanges/validate` answered for this ticket: the NIT is the reference the
  // traveller reads off their own ticket, and `openTicket` is the backend's word on what kind of
  // change this is. Read field by field, like the slices above.
  const ticketNit = useSelector((state) => state.exchange.ticket?.nit);
  const isOpenTicket = useSelector((state) => state.exchange.ticket?.openTicket);
  const ticketEmail = useSelector((state) => state.exchange.ticket?.email);
  const exchangePassengers = useSelector((state) => state.exchange.passengers);
  const selectedPassengerToAlter = useSelector((state) => state.exchange.selectedPassengerToAlter);

  // Which ticket this exchange is against, resolved exactly as the legacy original-ticket card
  // resolves it. The summary used to render every ticket of both legs, which told the traveller
  // they were changing four tickets when they had picked one.
  const { ticket: selectedTicket, isReturnTrip: isReturnTicket } = resolveSelectedExchangeTicket({
    selectedPassengerToAlter,
    allPassengers,
    passengers: exchangePassengers,
  });
  const dispatch = useDispatch();

  const history = useHistory();
  const location = useLocation();
  const { operationNumbers, NIT, origin, destination } = useParams();
  // Task 8.5 (openspec/changes/iamsa-b2c-ticket-exchange, platform-sre): the chosen transporter
  // travels as a `?transporter=` query param (see `createSchedulePath`) so it survives a direct
  // reload of this route, not just an in-app navigation from ExchangeFirstStep.
  // Only tenants that resolve their line explicitly carry one; for everyone else the param is
  // never produced and is ignored here even if a hand-made URL supplies it.
  const lineSelectionEnabled = Boolean(features.EXCHANGE_LINE_SELECTION_ENABLED);
  const transporterParam = new URLSearchParams(location.search).get('transporter') || undefined;
  const transporter = lineSelectionEnabled ? transporterParam : undefined;

  // The abbr in the URL can be stale: a line's `allow_changes` switch is flipped without a
  // deploy, and a schedule URL can be shared or reloaded much later. `POST /search` answers
  // 200 with every line's trips when it can't resolve the transporter instead of erroring, so
  // an unchecked abbr silently produces an unscoped trip list — caught only by the 422 when
  // the purchase is created. Check it against `exchanges/lines` before letting the search run.
  useEffect(() => {
    if (transporter) dispatch(fetchExchangeableLines());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `exchange.transporter` is only stored on EXCHANGE_RECEIVE — after `exchanges/validate`
  // answered 200 for this very abbr — and the resolver rejects an abbr it can't resolve rather
  // than falling back to another line. A stored value matching the URL is therefore an
  // independent confirmation, and the way out when it's the lines request that failed.
  const confirmedByRevalidation =
    Boolean(confirmedTransporter) && confirmedTransporter === transporter;

  /**
   * State of the `?transporter=` abbr. Starts as 'checking' whenever the URL carries one, so
   * the search is gated from the very first render — `ExchangeTripList`'s auto-search effect
   * runs before this component's own effects.
   */
  let transporterStatus = 'absent';
  if (transporter) {
    if (linesLoading) {
      transporterStatus = 'checking';
    } else if (lines.length) {
      transporterStatus = lines.some((line) => line.transporterAbbr === transporter)
        ? 'valid'
        : 'invalid';
    } else if (confirmedByRevalidation) {
      transporterStatus = 'valid';
    } else {
      // Nothing trustworthy yet: the lines request failed, or hasn't landed. Stay blocked —
      // `POST /search` ignores an abbr it can't resolve and answers 200 with every line's
      // trips, so searching now can offer trips the user must not be able to pick. The
      // revalidation unblocks this as soon as it confirms the abbr, or redirects on 422.
      transporterStatus = 'checking';
    }
  }
  // Never search with an abbr that is unconfirmed or known to be dead: an unscoped list would
  // let the user pick a trip from another line, and nothing would catch it until the purchase.
  const canSearch = transporterStatus !== 'checking' && transporterStatus !== 'invalid';

  // The line being exchanged with, for the mark beside the title. Resolved from the same abbr the
  // search is scoped to, falling back to the one `exchanges/validate` confirmed so a reload that
  // lost the param still shows it. `lines` is what carries the commercial name and the logo — the
  // abbr is never shown. Tenants without the flag never resolve a line and never get a mark.
  const markTransporter = lineSelectionEnabled ? transporter || confirmedTransporter : undefined;
  const selectedLine =
    markTransporter && lines.find((line) => line.transporterAbbr === markTransporter);

  const placesBaseUrl = `${env.api.searchUrl}/v2/places`;

  const [originInput, setOriginInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [originUrl, setOriginUrl] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [tripSelected, setTripSelected] = useState(false);
  // A logo the CDN does not serve is a line with no logo: fall back to the name rather than to
  // the browser's broken-image mark next to the title.
  const [lineLogoFailed, setLineLogoFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const usingRecaptcha = features.EXCHANGE_REQUIRE_RECAPTCHA && env.recaptcha;

  const tripHaveSelected = useRef();

  const { maxDaysSearch } = env.searchWidget;
  // Get the current date
  const currentDate = moment();
  // Add the max days search days to the current date
  const maxDate = maxDaysSearch && currentDate.add(maxDaysSearch, 'days').toDate();

  /**
   * Function to handle the recaptcha token
   */
  const onCaptchaVerify = (onRecaptchaToken) => {
    const action = features.EXCHANGE_RECAPTCHA_ACTION;
    executeRecaptcha(action, (recaptchaToken) => {
      onRecaptchaToken(recaptchaToken);
    });
  };

  useEffect(() => {
    if (currentTicket === '') {
      const operationNumbersArray = operationNumbers.split(',');

      /**
       * Function to validate the exchange wrapping the recaptcha token if needed
       * @param {*} [recaptchaToken] - Recaptcha token
       * @returns {function} - Dispatch function
       */
      const validationFunction = (recaptchaToken) =>
        validateExchange(
          history,
          operationNumbersArray,
          NIT,
          origin,
          destination,
          recaptchaToken,
          undefined,
          undefined,
          transporter,
        );

      if (usingRecaptcha) {
        onCaptchaVerify(validationFunction);
      } else {
        validationFunction();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (originInput !== '' && originDisplay === '') {
      setOriginUrl(`${placesBaseUrl}?q=${originInput}`);
    }
  }, [originInput, placesBaseUrl, originDisplay]);

  useEffect(() => {
    if (destinationInput !== '' && destinationDisplay === '') {
      setDestinationUrl(`${placesBaseUrl}?q=${destinationInput}&from=${originSlug}`);
    }
  }, [destinationDisplay, destinationInput, originSlug, placesBaseUrl]);

  const isDestinationDisabled = !originSlug;

  useEffect(() => {
    if (originDisplay !== '') {
      setOriginInput(originDisplay);
    }
  }, [originDisplay]);

  useEffect(() => {
    if (destinationDisplay !== '') {
      setDestinationInput(destinationDisplay);
    }
  }, [destinationDisplay]);

  const handlePurchaseRedirect = useCallback(() => {
    if (!isFetching && token && tripSelected) {
      // Under the redesign the seats are chosen on the schedule list itself, and they travel to the
      // purchase as `preSelectedSeats`. Sending the traveller to the seats step anyway would ask
      // them to pick the same seats a second time.
      //
      // The only question asked here is whether seats were actually chosen — deliberately nothing
      // about the shape of the trip. Should a seat turn out to be missing after all, `Passengers`
      // sends the traveller back through `redirectToSeatsIfNeeded`, which checks each way against
      // the real purchase rather than against an assumption made here.
      const skipSeatStep = useNewExchangeUi && hasSelectedSeats;

      history.push(`/purchase/${token}/${skipSeatStep ? 'passengers' : 'seats/departure'}`);
    }
  }, [history, isFetching, token, tripSelected, useNewExchangeUi, hasSelectedSeats]);

  useEffect(() => {
    handlePurchaseRedirect();
  }, [handlePurchaseRedirect]);

  useEffect(() => {
    if (errorTrip && !tripHaveSelected.current) {
      errorTripExchange(false);
      setIsLoading(false);
      setTripSelected(false);
      tripHaveSelected.current = false;
      history.push(
        createSchedulePath(
          operationNumbers,
          NIT,
          origin,
          destination,
          undefined,
          undefined,
          transporter,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorTrip]);

  const clearTypeaheadData = (origin = false, destination = false) => {
    clearTypeahead(origin, destination);
  };

  const handleKeyUp = (control, event) => {
    event.persist();

    if (control === 'origin' && originSlug !== '') {
      clearTypeaheadData(true, true);
      setDestinationInput('');
    }

    if (control === 'destination' && destinationSlug !== '') {
      clearTypeaheadData(false, true);
    }
  };

  // Where "another ticket" is chosen. A tenant with `USE_EXCHANGE_TICKET_LIST_ROUTE` picked this
  // one from its ticket list, so that list is where it goes back to — the same destination the
  // current layout's "Regresar a boletos" reaches, built with the same shared builder rather than
  // walked back through browser history. Every other tenant starts the exchange over at the form,
  // which is what this control has always done for them.
  const ticketListPath =
    location.state?.exchangeTicketListPath ||
    (features.USE_EXCHANGE_TICKET_LIST_ROUTE
      ? // Reached directly rather than from the list, so its address has to be rebuilt. Without
        // the document segment: a tenant that needs one arrives with the remembered path above,
        // and one that does not cannot load its list with a document it never used.
        createSchedulePath(
          operationNumbers,
          undefined,
          undefined,
          undefined,
          ticketEmail,
          undefined,
          markTransporter,
        )
      : null);

  /**
   * Sends the traveller back to where a different ticket is chosen: the ticket list when there is
   * one (`ticketListPath`), the exchange form otherwise.
   *
   * The form case takes the same two steps the sidebar's control has always taken — clear the
   * exchange, then go to the form — because it is the same journey. The redesign drops the
   * sidebar, so without this the only way back was the browser's own button.
   */
  const handleChangeAnotherTicket = () => {
    if (ticketListPath) {
      // No reset: the list is re-entered with the exchange it validated, as going back would.
      history.push(ticketListPath);
      return;
    }
    dispatch(resetExchange());
    history.push('/exchange');
  };

  /**
   * The "change another ticket" block, sitting in the ticket grid as one more card.
   *
   * A dashed outline rather than a filled card: it is an empty slot inviting another ticket, not a
   * ticket. Its colour is the brand's, like every other accent on this screen.
   * @returns {JSX.Element}
   */
  const renderChangeAnotherTicket = () => (
    <button
      type="button"
      onClick={handleChangeAnotherTicket}
      className="flex h-full min-h-[110px] w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-grayMedium bg-transparent p-4 transition-colors duration-200 hover:bg-primary-regular/5"
    >
      <Icon type="BackArrow" size="S" color="primary" />
      <Text color="primary" weight="bold" size="S">
        {ticketListPath ? t('button.back_to_tickets') : t('labels.change_schedule')}
      </Text>
    </button>
  );

  /**
   * The chosen line, beside the title: its logo, or its commercial name when it has none.
   *
   * Only tenants that resolve their line explicitly (EXCHANGE_LINE_SELECTION_ENABLED) have one to
   * show — everyone else exchanges through `tenant.exchange_transporter`, where the line is the
   * brand itself and a mark would say nothing.
   * @returns {JSX.Element|null}
   */
  const renderLineMark = () => {
    if (!selectedLine) return null;
    if (selectedLine.logoUrl && !lineLogoFailed) {
      return (
        <img
          src={selectedLine.logoUrl}
          alt={selectedLine.name}
          className="h-7 max-w-[140px] flex-shrink-0 object-contain"
          onError={() => setLineLogoFailed(true)}
        />
      );
    }
    if (!selectedLine.name) return null;
    return (
      <Text weight="bold" color="grayMedium">
        {selectedLine.name}
      </Text>
    );
  };

  /**
   * Title and ticket reference above the summary.
   *
   * The title follows the backend, not the URL: `open_ticket` is what says whether this is an open
   * ticket being redeemed or a schedule being moved, and `/exchange/openticket` can be reached with
   * either. The NIT line is omitted where there is none — it is IAMSA's reference, and a tenant
   * that identifies a ticket some other way should not be shown an empty label.
   * @returns {JSX.Element}
   */
  const renderSummaryHeading = () => (
    <div className="flex items-start justify-between gap-4 max-w-[600px]">
      <Spacing vertical size="XXS">
        <Text size="XL" weight="bold">
          {t(isOpenTicket ? 'title.exchange_open_ticket' : 'title.change_your_schedule')}
        </Text>
        {Boolean(ticketNit) && (
          <Text color="grayMedium">{`${t('labels.input_nit_number')} ${ticketNit}`}</Text>
        )}
      </Spacing>
      {renderLineMark()}
    </div>
  );

  /**
   * The tickets being exchanged, grouped by leg. Each group renders nothing when its leg carries
   * no tickets, so a tenant with no return leg gets a single group without a branch here.
   * @returns {JSX.Element|null}
   */
  const renderTicketSummary = () => {
    if (!useNewExchangeUi) return null;

    // One ticket: the one being changed, on the leg it belongs to. A tenant whose flow never
    // picks one falls back to the first passenger, and the leg to the outbound, which is what
    // the resolver answers and what the current layout shows.
    const summaryTrip = isReturnTicket ? trips.tripReturn : trips.tripDepart;

    return (
      <Spacing vertical size="S">
        {renderSummaryHeading()}
        <TicketSummaryContainer
          action={renderChangeAnotherTicket()}
          isReturnTrip={isReturnTicket}
          originTerminal={summaryTrip?.originDisplay}
          destinationTerminal={summaryTrip?.destinationDisplay}
          date={summaryTrip?.date}
          time={summaryTrip?.time}
          passengers={selectedTicket ? [selectedTicket] : []}
          avatarForTicket={avatarForTicket}
          selectable={false}
        />
      </Spacing>
    );
  };

  return (
    <>
      {(tripSelected || isLoading) && <Loader />}
      <div style={tripSelected || isLoading ? { display: 'none' } : {}}>
        <Spacing vertical>
          {renderTicketSummary()}
          {!showSearchWidget && (
            <>
              <SectionTitle title={t('labels.trip_schedule')} />
              {features.CAN_EXCHANGE_CHANGE_PLACES ? (
                <Spacing flexGrow>
                  <ExchangeTypeahead
                    label={t('labels.origin')}
                    name="origin"
                    onChange={setOriginInput}
                    url={originUrl}
                    value={originInput}
                    onKeyUp={handleKeyUp}
                  />
                  <ExchangeTypeahead
                    label={t('labels.destination')}
                    name="destination"
                    onChange={setDestinationInput}
                    url={destinationUrl}
                    value={destinationInput}
                    isDisabled={isDestinationDisabled}
                    onKeyUp={handleKeyUp}
                  />
                </Spacing>
              ) : (
                <Spacing flexGrow>
                  <Alert alertType="info" title={t('info.change_places')} />
                </Spacing>
              )}
              <div className="exchange-container-departure">
                <Text weight="semibold">{t('labels.trip_date')}</Text>
                <DatePicker
                  onChange={setDate}
                  value={date}
                  input={<ExchangeDatePicker />}
                  max={maxDate}
                  modal
                />
              </div>
            </>
          )}
          <Spacing vertical>
            <div>
              <ExchangeTripList
                originSlug={originSlug}
                destinationSlug={destinationSlug}
                date={date}
                onSelectTrip={setTripSelected}
                setIsLoading={setIsLoading}
                // Sourced from the URL rather than from the revalidation's redux state: on a
                // direct reload the search would otherwise start before `validateExchange`
                // resolves and go out unscoped.
                transporter={transporterStatus === 'invalid' ? undefined : transporter}
                canSearch={canSearch}
              />
            </div>
          </Spacing>
        </Spacing>
      </div>
    </>
  );
};

ExchangeSecondStep.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
  hasSelectedSeats: PropTypes.bool,
  originSlug: PropTypes.string.isRequired,
  originDisplay: PropTypes.string.isRequired,
  destinationDisplay: PropTypes.string.isRequired,
  destinationSlug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  currentTicket: PropTypes.string.isRequired,
  clearTypeahead: PropTypes.func.isRequired,
  errorTrip: PropTypes.bool,
  errorTripExchange: PropTypes.func,
  validateExchange: PropTypes.func,
  setDate: PropTypes.func,
};

export default ExchangeSecondStep;
